/**
 * FieldSpecialist.js — Module 3 · Field Engagement Dashboard (Shayla)
 * Route: /field-specialist
 *
 * Live MediaStream camera with:
 *   - facingMode: "environment" (rear wide-angle)
 *   - programmatic torch (Android Chrome ImageCapture) for low-light illumination
 *   - ambient luminance sampling → auto-torch recommendation
 *   - one-tap check-in
 *   - voice-to-text (Web Speech API) notes overlay
 *
 * Terminology: digital platform · never AI · never autonomous
 */
import React, { useState, useRef, useEffect } from "react";
import CrewJobCard from "./components/CrewJobCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const NAVY = "#020A1F"; const CYAN = "#00CFCF"; const CHROME = "#A0A0A0"; const INK = "#F5F5F7";

const shell = { minHeight: "100dvh", background: NAVY, color: INK, padding: "clamp(12px,3vw,28px)", fontFamily: "'Inter', system-ui, sans-serif", boxSizing: "border-box" };
const card = { background: "rgba(255,255,255,0.03)", border: `1px solid ${CHROME}33`, borderRadius: 12, padding: "clamp(14px,2.5vw,22px)", marginBottom: 16 };
const btn = { background: CYAN, color: NAVY, border: 0, padding: "12px 18px", fontWeight: 700, borderRadius: 8, cursor: "pointer", fontSize: 13, letterSpacing: "0.03em" };
const btnGhost = { ...btn, background: "transparent", color: INK, border: `1px solid ${CHROME}55` };
const input = { width: "100%", background: "rgba(0,0,0,0.35)", border: `1px solid ${CHROME}44`, color: INK, padding: "10px 12px", borderRadius: 6, fontSize: 14, fontFamily: "inherit", boxSizing: "border-box" };

async function sampleLuminance(videoEl) {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 96; canvas.height = 54;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    let sum = 0;
    for (let i = 0; i < data.length; i += 4) {
      // Rec. 601 luma
      sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    }
    return sum / (data.length / 4);
  } catch (e) { return null; }
}

export default function FieldSpecialist() {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [address, setAddress] = useState("");
  const [zone, setZone] = useState("Kitchen");
  const [caption, setCaption] = useState("");
  const [voiceNote, setVoiceNote] = useState("");
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [luminance, setLuminance] = useState(null);
  const [torchRecommend, setTorchRecommend] = useState(null);
  const [cameraOn, setCameraOn] = useState(false);
  const [captures, setCaptures] = useState([]);
  const [status, setStatus] = useState("");

  useEffect(() => () => stopCamera(), []);

  const startCamera = async () => {
    setStatus("Starting camera…");
    try {
      // Ultra-wide 0.5x field of view · 16:9 aspect · environment facing.
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 3840 },
          height: { ideal: 2160 },
          aspectRatio: { ideal: 16 / 9 },
          advanced: [{ zoom: 0.5 }],
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      const track = stream.getVideoTracks()[0];
      const caps = track.getCapabilities ? track.getCapabilities() : {};
      setTorchAvailable(!!caps.torch);
      // If the camera supports zoom, apply the 0.5x constraint post-open (some
      // browsers ignore advanced constraints during getUserMedia).
      if (caps.zoom && typeof caps.zoom.min === "number") {
        try {
          const targetZoom = Math.max(caps.zoom.min, 0.5);
          await track.applyConstraints({ advanced: [{ zoom: targetZoom }] });
        } catch (_) { /* zoom optional */ }
      }
      setCameraOn(true);
      setStatus("Camera live · ultra-wide 0.5x · ready to capture.");
      // Sample ambient luminance once video starts playing
      setTimeout(async () => {
        if (videoRef.current) {
          const lum = await sampleLuminance(videoRef.current);
          setLuminance(lum);
          if (lum !== null) {
            const rec = await fetch(`${BACKEND_URL}/api/field-specialist/torch-check`, {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ ambient_luminance: lum }),
            }).then(r => r.json());
            setTorchRecommend(rec);
            if (rec.torch_recommended && caps.torch) toggleTorch(true);
          }
        }
      }, 800);
    } catch (e) { setStatus(`Camera unavailable: ${e.message}`); }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraOn(false); setTorchOn(false); setTorchAvailable(false);
  };

  const toggleTorch = async (force) => {
    if (!streamRef.current) return;
    const track = streamRef.current.getVideoTracks()[0];
    const target = force !== undefined ? force : !torchOn;
    try {
      await track.applyConstraints({ advanced: [{ torch: target }] });
      setTorchOn(target);
    } catch (e) { setStatus(`Torch unavailable: ${e.message}`); }
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !cameraOn) return;
    setStatus("Capturing…");
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth || 1920;
    canvas.height = videoRef.current.videoHeight || 1080;
    canvas.getContext("2d").drawImage(videoRef.current, 0, 0);
    const b64 = canvas.toDataURL("image/jpeg", 0.82);
    const res = await fetch(`${BACKEND_URL}/api/field-specialist/capture`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({
        property_address: address, zone, photo_b64: b64, caption,
        voice_to_text_note: voiceNote, ambient_luminance: luminance,
        torch_used: torchOn, facing_mode: "environment",
      }),
    });
    const data = await res.json();
    if (data.ok) {
      setCaptures([data.capture, ...captures].slice(0, 8));
      setCaption(""); setVoiceNote("");
      setStatus(`Capture ${data.capture.capture_id} sealed.`);
    } else {
      setStatus(`Capture failed: ${data.detail || "unknown"}`);
    }
  };

  const oneTapCheckin = async () => {
    if (!address) { setStatus("Enter property address first."); return; }
    let geo = {};
    try {
      const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 4000 }));
      geo = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy };
    } catch (_) { /* geolocation optional */ }
    const r = await fetch(`${BACKEND_URL}/api/field-specialist/checkin`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ property_address: address, geo_coords: geo }),
    });
    const d = await r.json();
    if (d.ok) setStatus(`Checked in · ${d.checkin.checkin_id}`);
  };

  const startVoiceDictation = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setStatus("Voice-to-text not available in this browser."); return; }
    const rec = new SR();
    rec.lang = "en-US"; rec.continuous = false; rec.interimResults = false;
    rec.onresult = e => setVoiceNote(prev => (prev ? prev + " " : "") + e.results[0][0].transcript);
    rec.onerror = e => setStatus(`Voice error: ${e.error}`);
    rec.start();
    setStatus("Listening…");
  };

  return (
    <div style={shell} data-testid="field-specialist-shell">
      <div style={{ ...card, borderColor: `${CYAN}44` }}>
        <div style={{ fontSize: 11, letterSpacing: "0.2em", color: CYAN, marginBottom: 6 }}>
          MOOVIN FORWARD LLC · FIELD ENGAGEMENT DASHBOARD
        </div>
        <h1 style={{ margin: 0, fontSize: "clamp(20px, 3vw, 26px)" }}>Wide-angle capture + low-light illumination</h1>
        <div style={{ color: CHROME, marginTop: 6, fontSize: 13 }}>
          Non-licensed field data collection. Torch triggers automatically in low-light zones
          (under-sink · utility closets · water heater bays · behind-appliance).
        </div>
      </div>

      <div style={card}>
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
          <label style={{ display: "block" }}>
            <div style={{ fontSize: 12, color: CHROME, marginBottom: 4 }}>Property Address *</div>
            <input style={input} value={address} onChange={e => setAddress(e.target.value)} data-testid="field-address-input"
              placeholder="5960 Siltstone Lane, Unit 932, Fort Worth TX 76137" />
          </label>
          <label style={{ display: "block" }}>
            <div style={{ fontSize: 12, color: CHROME, marginBottom: 4 }}>Zone</div>
            <select style={input} value={zone} onChange={e => setZone(e.target.value)} data-testid="field-zone-select">
              {["Entry", "Kitchen", "Bathroom", "Bedroom", "Living Room", "Utility", "Exterior", "Balcony"].map(z => (
                <option key={z} value={z}>{z}</option>
              ))}
            </select>
          </label>
        </div>
        <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button style={btn} onClick={oneTapCheckin} data-testid="field-checkin-btn">📍 One-Tap Check-In</button>
          {!cameraOn ? (
            <button style={btn} onClick={startCamera} data-testid="field-start-camera">▶ Start Camera</button>
          ) : (
            <button style={btnGhost} onClick={stopCamera} data-testid="field-stop-camera">■ Stop Camera</button>
          )}
        </div>
      </div>

      {cameraOn && (
        <div style={card}>
          <div style={{ position: "relative" }}>
            <video ref={videoRef} autoPlay playsInline muted style={{ width: "100%", borderRadius: 8, background: "#000", aspectRatio: "16/9" }} data-testid="field-video" />
            {/* Spatial leveling crosshair overlay */}
            <div data-testid="field-level-overlay" style={{
              position: "absolute", inset: 0, pointerEvents: "none",
              borderRadius: 8,
              background:
                "linear-gradient(to right, transparent calc(50% - 0.5px), rgba(0,207,207,0.35) calc(50% - 0.5px), rgba(0,207,207,0.35) calc(50% + 0.5px), transparent calc(50% + 0.5px)), " +
                "linear-gradient(to bottom, transparent calc(50% - 0.5px), rgba(0,207,207,0.35) calc(50% - 0.5px), rgba(0,207,207,0.35) calc(50% + 0.5px), transparent calc(50% + 0.5px))",
            }} />
            <div style={{
              position: "absolute", top: 10, left: 10, fontSize: 10,
              color: CYAN, background: "rgba(3,11,26,0.7)", padding: "2px 8px",
              borderRadius: 4, letterSpacing: "0.1em",
            }}>ULTRA-WIDE · 0.5x · 16:9 · LEVELING LOCK</div>
          </div>
          <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap", fontSize: 12, color: CHROME }}>
            {luminance !== null && <span data-testid="field-luminance">Ambient luminance: {luminance.toFixed(1)}</span>}
            {torchRecommend && torchRecommend.torch_recommended && (
              <span style={{ color: CYAN }} data-testid="field-torch-rec">⚡ Low-light detected · torch recommended</span>
            )}
            {torchAvailable && (
              <button style={{ ...btnGhost, padding: "4px 10px", fontSize: 11 }}
                onClick={() => toggleTorch()} data-testid="field-torch-toggle">
                {torchOn ? "⚡ Torch ON" : "○ Torch OFF"}
              </button>
            )}
            {!torchAvailable && cameraOn && (
              <span data-testid="field-torch-unavailable">
                Torch programmatic control unavailable — use device native flash if needed.
              </span>
            )}
          </div>

          <div style={{ marginTop: 12, display: "grid", gap: 10, gridTemplateColumns: "1fr" }}>
            <label>
              <div style={{ fontSize: 12, color: CHROME, marginBottom: 4 }}>Caption</div>
              <input style={input} value={caption} onChange={e => setCaption(e.target.value)} data-testid="field-caption-input" />
            </label>
            <label>
              <div style={{ fontSize: 12, color: CHROME, marginBottom: 4, display: "flex", justifyContent: "space-between" }}>
                <span>Voice-to-text note</span>
                <button style={{ ...btnGhost, padding: "2px 8px", fontSize: 11 }} onClick={startVoiceDictation} data-testid="field-voice-btn">
                  🎙 Dictate
                </button>
              </div>
              <textarea style={{ ...input, minHeight: 60 }} value={voiceNote} onChange={e => setVoiceNote(e.target.value)} data-testid="field-voice-note" />
            </label>
          </div>

          <div style={{ marginTop: 12 }}>
            <button style={btn} onClick={capturePhoto} disabled={!address} data-testid="field-capture-btn">
              📸 Capture · seal to vault
            </button>
          </div>
        </div>
      )}

      {status && <div style={{ ...card, fontSize: 13, color: CYAN }} data-testid="field-status">{status}</div>}

      {address && (
        <CrewJobCard crewEmail="shayla@moovinforward.com" propertyAddress={address} />
      )}

      {captures.length > 0 && (
        <div style={card} data-testid="field-recent-captures">
          <h3 style={{ marginTop: 0, color: CYAN, fontSize: 12, letterSpacing: "0.15em" }}>RECENT CAPTURES</h3>
          {captures.map(c => (
            <div key={c.capture_id} style={{ padding: "8px 0", borderTop: `1px solid ${CHROME}22`, fontSize: 12 }}>
              <div><strong>{c.capture_id}</strong> · {c.zone} · torch:{c.torch_used ? "on" : "off"} · {c.photo_bytes} bytes</div>
              <div style={{ color: CHROME }}>{c.caption}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
